import { useEffect, useState } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Bell, Send, Smartphone, Users, Globe, CheckCircle2, XCircle,
  AlertTriangle, RefreshCw, History,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ROLES = ["SUPER_ADMIN", "SALES_ADMIN", "FACTORY_ADMIN", "SALES_REP", "COUNTER_SALES_REP", "EMPLOYEE"];

const PushNotificationsPage = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [selectedRoles, setSelectedRoles] = useState(["SALES_REP"]);
  const [sending, setSending] = useState(false);
  const [testUserId, setTestUserId] = useState("");
  const [testing, setTesting] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await authFetch(`${API}/push/status`);
      const d = await res.json();
      if (d.success) setStatus(d.data);
    } catch {}
  };
  const fetchLogs = async () => {
    try {
      const res = await authFetch(`${API}/push/logs?limit=30`);
      const d = await res.json();
      if (d.success) setLogs(d.data || []);
    } catch {}
  };

  useEffect(() => { fetchStatus(); fetchLogs(); }, []);

  const toggleRole = (r) => {
    setSelectedRoles(cur => cur.includes(r) ? cur.filter(x => x !== r) : [...cur, r]);
  };

  const sendBroadcast = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and body are required"); return; }
    if (selectedRoles.length === 0) { toast.error("Pick at least one role"); return; }
    setSending(true);
    try {
      const res = await authFetch(`${API}/push/broadcast`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, body, roles: selectedRoles }),
      });
      const d = await res.json();
      if (d.success) {
        toast.success(`Broadcast sent`, { description: `${d.data.sent} delivered, ${d.data.failed} failed` });
        setTitle(""); setBody("");
        fetchLogs(); fetchStatus();
      } else {
        toast.error("Broadcast failed", { description: d.data?.error || "Unknown error" });
      }
    } catch { toast.error("Network error"); }
    finally { setSending(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const payload = { title: "Pravaah Test", body: "Test notification — " + new Date().toLocaleTimeString() };
      if (testUserId) payload.user_id = testUserId;
      const res = await authFetch(`${API}/push/test`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (d.success) toast.success("Test push sent", { description: `${d.data.sent} delivered, ${d.data.failed} failed` });
      else toast.error("Test failed", { description: d.data?.error?.slice(0, 150) || "No devices registered or token invalid" });
      fetchLogs();
    } catch { toast.error("Network error"); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6" data-testid="push-notifications-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Sora'] flex items-center gap-2">
          <Bell className="w-6 h-6 text-[#8B1A1A]" /> Push Notifications
        </h1>
        <p className="text-sm text-slate-500 mt-1">Firebase Cloud Messaging — broadcast alerts to mobile apps by role or region.</p>
      </div>

      {/* Status Banner */}
      {status && (
        <Card className={`p-4 border-l-4 ${status.fcm_configured ? "border-l-emerald-500 bg-emerald-50/30" : "border-l-red-500 bg-red-50/30"}`} data-testid="fcm-status">
          <div className="flex items-center gap-3">
            {status.fcm_configured ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
            <div className="flex-1">
              <p className="font-semibold text-sm">{status.fcm_configured ? "FCM Configured" : "FCM Not Ready"}</p>
              <p className="text-xs text-slate-600">
                {status.fcm_configured ? `Project: ${status.project_id} · ${status.active_device_tokens} active device${status.active_device_tokens !== 1 ? "s" : ""}` : status.error}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => { fetchStatus(); fetchLogs(); }} data-testid="refresh-status">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </Card>
      )}

      <Tabs defaultValue="broadcast">
        <TabsList>
          <TabsTrigger value="broadcast" data-testid="tab-broadcast"><Globe className="w-3.5 h-3.5 mr-1" /> Broadcast</TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test"><Smartphone className="w-3.5 h-3.5 mr-1" /> Test</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="w-3.5 h-3.5 mr-1" /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="broadcast" className="mt-4">
          <Card className="p-5 max-w-2xl">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Send className="w-4 h-4 text-[#8B1A1A]" /> Broadcast to Roles</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Title</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New Diwali Scheme Launched!" maxLength={65} data-testid="broadcast-title" />
                <p className="text-[10px] text-slate-400 mt-1">{title.length}/65</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Message</label>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" placeholder="Scheme details, call to action..." maxLength={240}
                  data-testid="broadcast-body" />
                <p className="text-[10px] text-slate-400 mt-1">{body.length}/240</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Target Roles</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ROLES.map(r => (
                    <button key={r} type="button" onClick={() => toggleRole(r)}
                      className={`px-3 py-1 rounded-full text-xs border transition ${selectedRoles.includes(r)
                        ? "bg-[#8B1A1A] text-white border-[#8B1A1A]"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                      data-testid={`role-${r}`}>
                      {r.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-2 flex items-center gap-2">
                <Button className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" onClick={sendBroadcast} disabled={sending || !status?.fcm_configured} data-testid="send-broadcast-btn">
                  {sending ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Broadcast</>}
                </Button>
                {!status?.fcm_configured && (
                  <span className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> FCM must be configured first</span>
                )}
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="test" className="mt-4">
          <Card className="p-5 max-w-xl">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Smartphone className="w-4 h-4 text-[#8B1A1A]" /> Send Test Notification</h3>
            <p className="text-xs text-slate-500 mb-4">Send a test push to yourself or a specific user to verify FCM integration is working.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 uppercase">Target User ID (optional)</label>
                <Input value={testUserId} onChange={(e) => setTestUserId(e.target.value)} placeholder="Leave empty to send to yourself" data-testid="test-user-id" />
              </div>
              <Button onClick={sendTest} disabled={testing || !status?.fcm_configured} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-test-btn">
                {testing ? "Sending..." : <><Send className="w-4 h-4 mr-2" /> Send Test</>}
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table text-sm" data-testid="push-logs-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Message</th>
                    <th>Target</th>
                    <th className="text-right">Sent</th>
                    <th className="text-right">Failed</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-10 text-slate-400">
                      <Bell className="w-6 h-6 mx-auto mb-2 opacity-30" />
                      No push notifications sent yet
                    </td></tr>
                  ) : logs.map((l, i) => (
                    <tr key={i} data-testid={`log-row-${i}`}>
                      <td className="font-medium">{l.title}</td>
                      <td className="text-slate-600 max-w-xs truncate">{l.body}</td>
                      <td>
                        {l.topics?.length > 0 && <Badge variant="outline" className="text-[10px] mr-1">{l.topics.join(", ")}</Badge>}
                        {l.user_ids?.length > 0 && <span className="text-[10px] text-slate-500">{l.user_ids.length} user(s)</span>}
                      </td>
                      <td className="text-right font-mono text-emerald-600 font-semibold">{l.sent}</td>
                      <td className="text-right font-mono text-red-500">{l.failed || 0}</td>
                      <td className="text-xs text-slate-500">{l.timestamp ? new Date(l.timestamp).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PushNotificationsPage;
